import {
  LightBulbIcon,
  ChartBarIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  BriefcaseIcon,
  ScaleIcon,
  UserIcon,
  ArrowTrendingUpIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { IconBrain, IconBullseye } from "../../../components/icons.generated";
import type { TrpcOutput } from "../../../utils/trpc.js";

type ProfileData = Extract<NonNullable<TrpcOutput["profile"]["getProfile"]>, { template: "nestle-1" }>["data"];

export function Nestle1Layout({ data }: { data: ProfileData }) {
  const { leadershipStyle } = data;

  return (
    <section className="space-y-10">
      {/* Leadership Level - Hero Card */}
      <div className="rounded-2xl border border-gray bg-white/60 p-6 shadow-lg shadow-lac-leman/10 backdrop-blur-xl md:p-8">
        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <div className="w-min shrink-0 rounded-xl bg-lac-leman/10 p-4">
            <UserIcon className="h-10 w-10 text-lac-leman" />
          </div>
          <div className="flex-1">
            <h2 className="text-2xl font-semibold text-lac-leman">{leadershipStyle.level}</h2>
            <p className="mt-3 text-sm leading-relaxed text-gray-80">{leadershipStyle.levelDescription}</p>
          </div>
        </div>
      </div>

      {/* What This Means */}
      <div className="space-y-5">
        <h3 className="flex items-center gap-3 text-2xl font-semibold">
          <LightBulbIcon className="h-6 w-6 text-lac-leman" />
          {leadershipStyle.whatThisMeans.title}
        </h3>
        <div className="rounded-2xl border border-gray bg-white/60 p-6 shadow-lg shadow-lac-leman/10 backdrop-blur-xl md:p-6">
          <div className="columns-1 gap-6 space-y-4 md:columns-2">
            {leadershipStyle.whatThisMeans.paragraphs.map((paragraph, index) => (
              <p key={index} className="break-inside-avoid text-sm leading-relaxed text-gray-80">
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* Core Capabilities */}
      <div className="">
        <h3 className="flex items-center gap-3 text-2xl font-semibold">
          <ChartBarIcon className="h-6 w-6 text-lac-leman" />
          {leadershipStyle.coreCapabilities.title}
        </h3>

        {/* Capabilities - 2x2 grid */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          {leadershipStyle.coreCapabilities.capabilities.map((capability, index) => (
            <div
              key={index}
              className="rounded-2xl border border-gray bg-white/60 p-5 shadow-lg shadow-lac-leman/10 backdrop-blur-xl">
              <div className="flex items-start gap-4">
                <div className="shrink-0 rounded-lg bg-lac-leman/10 p-2.5">
                  {index === 0 && <IconBrain className="h-6 w-6 text-[#65A2DB]" />}
                  {index === 1 && <IconBullseye className="h-6 w-6 text-[#D61F25]" />}
                  {index === 2 && <ArrowTrendingUpIcon className="h-6 w-6 text-[#16AD70]" />}
                  {index === 3 && <UsersIcon className="h-6 w-6 text-[#A78BFA]" />}
                  {index >= 4 && <Cog6ToothIcon className="h-6 w-6 text-lac-leman" />}
                </div>
                <div>
                  <h4 className="text-base font-semibold">{capability.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-gray-80">{capability.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Virtuous Cycle */}
        <div className="mb-6 mt-10 flex items-start gap-4">
          <div className="shrink-0 rounded-xl bg-lac-leman/10 p-3">
            <ArrowPathIcon className="h-8 w-8 text-lac-leman" />
          </div>
          <div>
            <h3 className="text-2xl font-semibold">{leadershipStyle.coreCapabilities.virtousCycle.title}</h3>
            <p className="mt-1 text-sm">{leadershipStyle.coreCapabilities.virtousCycle.subtitle}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {leadershipStyle.coreCapabilities.virtousCycle.items.map((item, index) => (
            <div
              key={index}
              className="to-gray-50/80 rounded-xl border border-gray bg-white/60 p-6 shadow-sm ring-1 ring-black/[0.03]">
              <div className="mb-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-lac-leman/10 px-3 py-1 text-xs font-medium text-lac-leman">
                  {item.tag1}
                </span>
                <span className="bg-gray-100 text-gray-600 rounded-full px-3 py-1 text-xs font-medium">
                  {item.tag2}
                </span>
              </div>
              <p className="text-sm leading-relaxed text-gray-80">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* In Practice */}
      <div className="space-y-5">
        <h3 className="flex items-center gap-3 text-2xl font-semibold">
          <BriefcaseIcon className="h-6 w-6 text-lac-leman" />
          {leadershipStyle.inPractice.title}
        </h3>

        <p className="mb-6 text-sm leading-relaxed">{leadershipStyle.inPractice.intro}</p>
        <div className="grid grid-cols-1 gap-4">
          {leadershipStyle.inPractice.items.map((item, index) => (
            <div
              key={index}
              className="to-gray-50/80 rounded-xl border border-gray bg-white/60 p-6 shadow-sm ring-1 ring-black/[0.03]">
              <h4 className="text-base font-semibold">{item.title}</h4>
              <p className="mt-2 text-sm leading-relaxed text-gray-80">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Balancing Note */}
      <div className="space-y-5">
        <h3 className="flex items-center gap-3 text-2xl font-semibold">
          <ScaleIcon className="h-6 w-6 text-lac-leman" />
          {leadershipStyle.balancingNote.title}
        </h3>

        <p className="mb-6 text-sm leading-relaxed">{leadershipStyle.balancingNote.subtitle}</p>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {leadershipStyle.balancingNote.items.map((item, index) => (
            <div
              key={index}
              className="to-gray-50/80 rounded-xl border border-gray bg-white/60 p-6 shadow-sm ring-1 ring-black/[0.03]">
              <p className="text-sm leading-relaxed">
                <span className="font-semibold">{item.boldLabel}</span>{" "}
                <span className="text-gray-80">{item.description}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
